import { usePolling } from "./usePolling";
import { useCallback, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  HK_STATUS,
  HousekeepingRequestDto,
  listHousekeepingRequests,
} from "../utils/housekeepingApi";

// Every open admin tab polls; usePolling skips hidden tabs and never overlaps
// requests. 20 s keeps alerts prompt without loading the server.
const POLL_INTERVAL_MS = 20_000;
const ACK_KEY = "hk-alert-acknowledged";

const readAcked = (): Set<number> | null => {
  try {
    const raw = localStorage.getItem(ACK_KEY);
    return raw === null ? null : new Set<number>(JSON.parse(raw));
  } catch {
    return null;
  }
};

const writeAcked = (ids: Set<number>) => {
  try {
    // Only the newest few hundred matter; older jobs are long since cleaned.
    localStorage.setItem(ACK_KEY, JSON.stringify(Array.from(ids).slice(-300)));
  } catch {
    // Storage blocked: the alert just comes back on the next reload.
  }
};

/** Watches for rooms newly sent to cleaning (a PENDING housekeeping job, opened by
 * check-out) and keeps the alert up until the desk acknowledges it. Mounted once,
 * in AdminLayout. The very first run on a browser treats the jobs already waiting
 * as seen, so the app does not blare over a backlog the first time it is opened. */
export const useHousekeepingAlerts = () => {
  const { user } = useAuth();
  const accountId = Number(user?.accountId) || 0;
  const [alerts, setAlerts] = useState<HousekeepingRequestDto[]>([]);

  const poll = useCallback(async () => {
    if (!accountId) return;
    try {
      const pending = await listHousekeepingRequests({
        accountId,
        statusCode: HK_STATUS.Pending,
      });
      let acked = readAcked();
      if (acked === null) {
        acked = new Set(pending.map((r) => r.housekeepingRequestId));
        writeAcked(acked);
      }
      const fresh = pending.filter((r) => !acked!.has(r.housekeepingRequestId));
      setAlerts((prev) =>
        prev.length === fresh.length &&
        prev.every((p, i) => p.housekeepingRequestId === fresh[i].housekeepingRequestId)
          ? prev
          : fresh
      );
    } catch {
      // Best-effort — the next tick tries again.
    }
  }, [accountId]);

  usePolling(poll, POLL_INTERVAL_MS);

  const acknowledgeAll = useCallback(() => {
    const acked = readAcked() ?? new Set<number>();
    alerts.forEach((r) => acked.add(r.housekeepingRequestId));
    writeAcked(acked);
    setAlerts([]);
  }, [alerts]);

  return { alerts, acknowledgeAll };
};
